import React, { Fragment, useEffect } from "react";
import { Seo, Breadcrumb, ScrollTopBtn } from "../components";
import { HeaderThree, FooterTwo } from "../containers";

export default function PrivacyPolicy() {
  // Scroll to top on load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Fragment>
      <Seo
        title="Privacy Policy - Tech Ayukta"
        description="Learn how Tech Ayukta collects, uses, and protects your personal information in compliance with India's DPDP, GDPR, and global data laws."
        keywords="Privacy Policy, Tech Ayukta, Data Protection, DPDP, GDPR, Personal Data, Information Security, User Consent"
        canonical="https://www.techayukta.com/privacy-policy"
      />

      <HeaderThree />
      <Breadcrumb
        pageName="Privacy Policy"
        bgImage="/images/breadcumb/breadcumb-bg.jpg"
      />

      <div className="container mx-auto px-4 px-md-5 py-5 py-md-6">
        <h1 className="text-3xl fw-bold mb-4 text-center text-md-start">
          Privacy Policy
        </h1>

        <p className="mb-4">
          At <strong>Tech Ayukta</strong>, your privacy and trust are of utmost importance. This Privacy Policy details how we collect, use, store, and safeguard your data in accordance with the <strong>Digital Personal Data Protection Act (India, 2023)</strong>, <strong>GDPR (EU)</strong>, and relevant global standards. Your rights, including those to withdraw consent, correction, and data deletion, are protected and explained here.
        </p>

        <h4 className="h5 fw-semibold mt-5 mb-2">1. Information We Collect</h4>
        <p className="mb-4">
          We collect only what is necessary for legitimate business purposes, including:
        </p>
        <ul className="mb-4">
          <li>Full name, email address, and contact number</li>
          <li>Business or educational details (if applicable)</li>
          <li>Payment or billing information for services/courses</li>
          <li>Technical data: browser type, IP address, device, cookies</li>
        </ul>

        <h4 className="h5 fw-semibold mt-5 mb-2">2. Purpose & Use of Data</h4>
        <p className="mb-4">
          Data is processed only for clear purposes, including:
        </p>
        <ul className="mb-4">
          <li>Service access, user account, course & transaction management</li>
          <li>Improving site and services</li>
          <li>Processing inquiries, registrations, payments, and support requests</li>
          <li>Sending service updates, offers, or technical/mandatory notifications</li>
        </ul>

        <h4 className="h5 fw-semibold mt-5 mb-2">3. Your Rights & Consent (DPDP Compliance)</h4>
        <p className="mb-4">
          Under the DPDP Act, you have the right to:
        </p>
        <ul className="mb-4">
          <li>Access your personal data</li>
          <li>Request correction or update</li>
          <li>Request deletion or anonymization</li>
          <li>Withdraw consent for non-essential processing at any time</li>
          <li>File a complaint with our Data Protection Officer (DPO) or the Data Protection Board of India</li>
        </ul>
        <p className="mb-4">
          Consent for non-essential cookies and communications is obtained transparently and may be managed via your dashboard or written request. For privacy-related concerns, email our DPO at <a href="mailto:privacy@techayukta.com" className="text-decoration-none text-primary">privacy@techayukta.com</a>.
        </p>

        <h4 className="h5 fw-semibold mt-5 mb-2">4. Data Security</h4>
        <p className="mb-4">
          Data is protected with industry-standard administrative, technical, and physical safeguards. All user transactions use <strong>SSL encryption</strong> and secure payment gateways. Access to data is strictly limited.
        </p>

        <h4 className="h5 fw-semibold mt-5 mb-2">5. Cookies and Tracking</h4>
        <p className="mb-4">
          We use necessary and optional cookies to improve the user experience, analyze site traffic, and personalize content. Consent banners inform you of our cookie policy. You may opt out of non-essential cookies. For detailed choices, review our <a href="/cookie-policy" className="text-decoration-none text-primary">Cookie Policy</a>.
        </p>

        <h4 className="h5 fw-semibold mt-5 mb-2">6. Third-Party Sharing & Processors</h4>
        <p className="mb-4">
          Personal data is never sold or rented. Data may be shared with trusted technology, analytics, payment, and communication partners under confidentiality and security agreements, and only as required to deliver our services.
        </p>

        <h4 className="h5 fw-semibold mt-5 mb-2">7. Data Retention</h4>
        <p className="mb-4">
          Data is retained for as long as required to serve the purposes collected, operate our business, or comply with legal requirements. When no longer needed, it is securely deleted or anonymized.
        </p>

        <h4 className="h5 fw-semibold mt-5 mb-2">8. Third-Party Tools & Integrations</h4>
        <p className="mb-4">
          We use third-party services (e.g., analytics, payment gateways) governed by their own privacy policies. Tech Ayukta conducts due diligence but recommends you review external privacy practices where applicable.
        </p>

        <h4 className="h5 fw-semibold mt-5 mb-2">9. Children’s Privacy</h4>
        <p className="mb-4">
          Our services are not intended for users under 16. We do not knowingly collect information from minors, and will promptly delete such data if discovered.
        </p>

        <h4 className="h5 fw-semibold mt-5 mb-2">10. International Transfers</h4>
        <p className="mb-4">
          Where required, Tech Ayukta complies with cross-border data transfer laws. International transfers are performed with safeguards or recognized security mechanisms.
        </p>

        <h4 className="h5 fw-semibold mt-5 mb-2">11. Accessibility</h4>
        <p className="mb-4">
          We strive to make privacy practices transparent and accessible to all users. If you require information in an alternative format, please contact us for assistance.
        </p>

        <h4 className="h5 fw-semibold mt-5 mb-2">12. Updates & Notice of Changes</h4>
        <p className="mb-4">
          This policy is reviewed periodically and updated based on legal, technological, or business changes. We will notify users of significant changes via email or website banner. "Last Updated" will reflect the current revision.
        </p>

        <p className="mt-5 text-sm">
          For any privacy-specific questions, requests, or complaints, contact our <a href="mailto:techsupport@techayukta.com" className="text-decoration-none text-primary">Data Protection Officer</a> or support at{" "}
          <a href="mailto:techsupport@techayukta.com" className="text-decoration-none text-primary">
            techsupport@techayukta.com
          </a>.
        </p>
      </div>

      <FooterTwo />
      <ScrollTopBtn />
    </Fragment>
  );
}
